import React, { useEffect, useRef, useState } from "react";
import { GlassCard } from "../components/ui/glass-card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import api, { persistAuthSession } from "../services/api";
import { useModal } from "../contexts/ModalContext";
import { useHistory } from "react-router";
import { useToast } from "../components/ui/use-toast";
import { SplashScreen } from "@capacitor/splash-screen";

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const history = useHistory();
  const { showPrompt } = useModal();
  const { toast } = useToast();

  const handleLogin = async () => {
    const currentEmail = emailRef.current?.value || email;
    const currentPassword = passwordRef.current?.value || password;

    if (isLoading) return;
    if (!currentEmail.trim() || !currentPassword.trim()) {
      toast({
        title: "Errore",
        description: "Inserisci email e password",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const res = await api.post("/login", {
        email: currentEmail.trim(),
        password: currentPassword,
      });

      persistAuthSession(res.data);

      history.push("/loading");
    } catch (e: any) {
      console.log(JSON.stringify(e));
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429) {
        toast({
          title: "Troppi tentativi",
          description:
            typeof detail === "string"
              ? detail
              : "Riprova tra qualche minuto.",
          variant: "error",
        });
      } else if (e?.response?.status === 403 && detail === "TEMP_EXPIRED") {
        toast({
          title: "Account scaduto",
          description:
            "Il periodo di prova è terminato. Usa 'Password dimenticata' per recuperare l'accesso.",
          variant: "error",
        });
        history.push("/forgot-password");
      } else if (e?.response?.status === 403) {
        toast({
          title: "Errore",
          description: "Email non verificata. Controlla la posta.",
          variant: "error",
        });
      } else {
        toast({
          title: "Errore",
          description: "Email o password non validi",
          variant: "error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      history.push("/loading");
    }
  }, [history]);

  //PRELOAD VIDEO LOADING
  useEffect(() => {
    const video = document.createElement("video");
    video.src = "/assets/loader.mp4";
    video.preload = "auto";
    video.load();
    return () => {
      video.src = "";
      SplashScreen.hide();
    };
  }, []);

  return (
    <div className="flex items-center justify-center min-h-screen p-4 relative overflow-auto">
      {/* sfere sfumate */}
      <div className="absolute top-1/5 left-1/5 w-48 h-48 rounded-full bg-teal-400/10 blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/5 right-1/5 w-72 h-72 rounded-full bg-teal-300/10 blur-3xl animate-pulse"></div>

      <GlassCard hover={false} className="max-w-md w-full p-8 space-y-6 z-10">
        <div className="flex justify-center mb-6">
          <img
            src="../assets/long_light.png"
            alt="logo"
            className="h-12 rounded-lg"
          />
        </div>

        <h1 className="text-center text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-teal-200 to-teal-500">
          Accedi
        </h1>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              ref={emailRef}
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              placeholder="mario@effia.com"
              className="backdrop-blur-xl bg-white/5 border border-white/10 shadow-xl"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              ref={passwordRef}
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              placeholder="••••••••"
              className="backdrop-blur-xl bg-white/5 border border-white/10 shadow-xl"
            />
          </div>
        </div>

        <Button
          onClick={handleLogin}
          disabled={isLoading}
          className="w-full font-bold h-12 bg-gradient-to-r from-teal-200 to-teal-500 text-white shadow-lg hover:opacity-90"
        >
          {isLoading ? "Accedendo..." : "Accedi"}
        </Button>

        <div className="flex justify-between text-sm mt-4">
          <button
            onClick={() => history.push("/forgot-password")}
            className="text-teal-400 hover:underline"
          >
            Password dimenticata?
          </button>
          <a href="/register" className="text-teal-400 hover:underline">
            Registrati
          </a>
        </div>
      </GlassCard>
    </div>
  );
};

export default Login;
